/**
 * Global handler for 401 Unauthorized errors
 * Used to communicate between QueryClient error handlers and AuthProvider
 */

import { clearAllAuthData } from "./model/oauth-flow"

type UnauthorizedHandler = (error: { status: number; message?: string }) => void

let unauthorizedHandler: UnauthorizedHandler | null = null

/**
 * Register handler for 401 errors
 * Should be called from AuthProvider on mount
 */
export function registerUnauthorizedHandler(handler: UnauthorizedHandler) {
	unauthorizedHandler = handler
}

/**
 * Unregister handler for 401 errors
 * Should be called from AuthProvider on unmount
 */
export function unregisterUnauthorizedHandler() {
	unauthorizedHandler = null
}

/**
 * Handle 401 error - call registered handler if exists
 * Should be called from QueryClient error handlers
 * Also clears authentication data (localStorage and cookie)
 */
export async function handleUnauthorizedError(error: unknown) {
	// Check if error has status 401
	const status =
		error && typeof error === "object" && "status" in error
			? (error as { status?: unknown }).status
			: null

	if (status === 401) {
		// Clear authentication data
		await clearAllAuthData()

		const message =
			error && typeof error === "object" && "message" in error
				? String((error as { message?: unknown }).message)
				: undefined

		// Notify handler if registered
		if (unauthorizedHandler) {
			unauthorizedHandler({
				status: 401,
				message,
			})
		}
	}
}

/**
 * Check if error is 401 Unauthorized
 */
export function isUnauthorizedError(error: unknown): boolean {
	if (error && typeof error === "object" && "status" in error) {
		return (error as { status?: unknown }).status === 401
	}
	return false
}
